import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Label } from 'shadcn/components/ui/Label';

import { useQueryParams } from '../hooks/useQueryParams';

/**
 * Dialog for duplicating a carousel to a list of aisles.
 *
 * @param {Object} carousel - The carousel to duplicate.
 * @param {boolean} isOpen - Whether the dialog is open.
 * @param {Function} onClose - Function to close the dialog.
 * @param {Function} refetchCarousels - Function to refetch carousels.
 * @param {Array} sortedAisles - The list of aisles to duplicate to.
 */
export default function DuplicateToAislesDialog({ carousel, isOpen, onClose, refetchCarousels, sortedAisles }) {
    const { queryParamsData } = useQueryParams();

    const [selectedAisles, setSelectedAisles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const dialogRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedAisles([]);
        }
    }, [isOpen]);

    const handleCheckedChange = (checked, aisleId) => {
        if (checked) {
            setSelectedAisles([...selectedAisles, aisleId]);
        } else {
            setSelectedAisles(selectedAisles.filter((id) => id !== aisleId));
        }
    };

    const handleSelectAllClick = () => {
        setSelectedAisles(selectedAisles.length ? [] : sortedAisles.map((aisle) => aisle.id));
    };

    const handleDuplicate = async () => {
        setIsLoading(true);
        const toastID = toast.loading('Duplicating carousel...');

        try {
            const carousels = selectedAisles.map((aisleId) => {
                const aisle = sortedAisles.find((a) => a.id === aisleId);

                if (!aisle) {
                    return null;
                }

                return {
                    ...carousel,
                    id: null,
                    l1AisleID: aisle.level === 1 ? aisle.id : null,
                    l2AisleID: aisle.level === 2 ? aisle.id : null,
                    rankNumber: null,
                    // * The GET /carousels endpoint returns `linkTitle`, whereas the PUT/POST endpoints expect `linkText`.
                    // * The GET /carousels endpoint returns `photoUrlMedium`, whereas the PUT/POST endpoints expect `imageUrl`.
                    // * The GET /carousels endpoint returns items with the id set to `carousel_sellable_item_map.id`,
                    //   whereas the PUT/POST endpoints expect the id to be the `sellable_item.id`.
                    imageUrl: carousel.photoUrlMedium,
                    linkText: carousel.linkTitle,
                    items: carousel.items.map((item) => ({
                        ...item,
                        id: item.sellableItemID ? item.sellableItemID : item.id,
                    })),
                };
            });

            await productionApi.createUpdateCarouselV2({
                packGroupID: queryParamsData.packGroupID,
                carousels,
            });
            onClose();
            toast.success('Carousel duplicated successfully');
            refetchCarousels();
        } catch (error) {
            console.error(error);
            toast.error('Failed to duplicate carousel');
        } finally {
            setIsLoading(false);
            toast.dismiss(toastID);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent ref={dialogRef} className="flex flex-col sm:max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle>Duplicate Carousel</DialogTitle>
                    <DialogDescription>
                        This action will create a new carousel with the same content within the specified aisles.
                        <br />
                        <br />
                        <span className="font-bold">
                            This carousel&apos;s rank will be cleared during the duplication process.
                        </span>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col h-full border rounded-md overflow-hidden">
                    <div className="text-sm font-medium border-b p-2 flex items-center justify-between">
                        Aisles
                        <Button variant="outline" size="sm" onClick={handleSelectAllClick}>
                            {selectedAisles.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </div>
                    <div className="flex flex-col gap-3 p-2 h-full overflow-y-auto">
                        {sortedAisles?.map((aisle) => (
                            <div key={aisle.id} className="flex items-center gap-3">
                                <Checkbox
                                    id={`duplicate-to-aisle-${aisle.id}`}
                                    checked={selectedAisles.includes(aisle.id)}
                                    onCheckedChange={(checked) => handleCheckedChange(checked, aisle.id)}
                                />
                                <Label
                                    htmlFor={`duplicate-to-aisle-${aisle.id}`}
                                    className="w-full font-normal cursor-pointer"
                                    style={{ paddingLeft: `${(aisle.level - 1) * 24}px` }}
                                >
                                    <span className="text-muted-foreground">{aisle.id}</span> {aisle.name}
                                </Label>
                            </div>
                        ))}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="default" onClick={handleDuplicate} disabled={isLoading}>
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
